import { useEffect, useState, useRef, useCallback } from "react";
import {
  ChatRoom,
  ChatMessage,
  DisconnectReason,
  SendMessageRequest,
} from "amazon-ivs-chat-messaging";
import { getChatToken, getChatHistory } from "@src/apis/chatApis";
import { IVS_CHAT_REGION } from "@src/constants/ivsChat";
import {
  mergeChatMessages,
  getMessageRequestId,
} from "@src/pages/Messages/conversationUtils";

interface IVSChatState {
  messages: Record<string, unknown>[];
  isConnected: boolean;
  isConnecting: boolean;
  isLoadingHistory: boolean;
  error: string | null;
}

interface UseIVSChatOptions {
  /** When false, only loads REST history (no IVS token / websocket). */
  enableRealtime?: boolean;
  /** Called when a live message arrives (e.g. refresh sidebar previews). */
  onLiveMessage?: () => void;
}

export const useIVSChat = (
  conversationId: string | null,
  options: UseIVSChatOptions = {}
) => {
  const { enableRealtime = true, onLiveMessage } = options;

  const [state, setState] = useState<IVSChatState>({
    messages: [],
    isConnected: false,
    isConnecting: false,
    isLoadingHistory: false,
    error: null,
  });

  const roomRef = useRef<ChatRoom | null>(null);
  const isConnectedRef = useRef(false);
  const onLiveMessageRef = useRef(onLiveMessage);

  useEffect(() => {
    onLiveMessageRef.current = onLiveMessage;
  }, [onLiveMessage]);

  const safeSetState = useCallback(
    (updater: (prev: IVSChatState) => IVSChatState) => {
      setState((prev) => updater(prev));
    },
    []
  );

  useEffect(() => {
    if (!conversationId) return;

    let cancelled = false;
    const unsubscribeFns: Array<() => void> = [];

    const connectToRoom = async () => {
      try {
        safeSetState((s) => ({
          ...s,
          isLoadingHistory: true,
          isConnecting: enableRealtime,
          error: null,
        }));

        const history = await getChatHistory(conversationId);
        if (cancelled) return;

        safeSetState((s) => ({
          ...s,
          messages: history,
          isLoadingHistory: false,
        }));

        if (!enableRealtime) {
          safeSetState((s) => ({ ...s, isConnecting: false }));
          return;
        }

        const room = new ChatRoom({
          regionOrUrl: IVS_CHAT_REGION,
          tokenProvider: async () => {
            const data = await getChatToken(conversationId);
            return {
              token: data.token,
              sessionExpirationTime: new Date(data.sessionExpirationTime),
              tokenExpirationTime: new Date(data.tokenExpirationTime),
            };
          },
        });

        if (cancelled) {
          room.disconnect();
          return;
        }

        roomRef.current = room;
        isConnectedRef.current = false;

        unsubscribeFns.push(
          room.addListener("connect", () => {
            if (cancelled) return;
            isConnectedRef.current = true;
            safeSetState((s) => ({
              ...s,
              isConnected: true,
              isConnecting: false,
              error: null,
            }));
          })
        );

        unsubscribeFns.push(
          room.addListener("disconnect", (_reason: DisconnectReason) => {
            if (cancelled) return;
            isConnectedRef.current = false;
            safeSetState((s) => ({ ...s, isConnected: false }));
          })
        );

        unsubscribeFns.push(
          room.addListener("message", (message: ChatMessage) => {
            if (cancelled) return;

            if (message.attributes?.roomId !== conversationId) {
              if (message.attributes?.roomId) {
                return;
              }
            }

            safeSetState((s) => ({
              ...s,
              messages: mergeChatMessages(
                s.messages,
                message as unknown as Record<string, unknown>
              ),
            }));

            onLiveMessageRef.current?.();
          })
        );

        await room.connect();
      } catch (err: any) {
        if (cancelled) return;
        isConnectedRef.current = false;
        // eslint-disable-next-line no-console
        console.error("IVS Chat Error:", err);
        safeSetState((s) => ({
          ...s,
          isLoadingHistory: false,
          isConnecting: false,
          isConnected: false,
          error: err.message || "Failed to connect to chat",
        }));
      }
    };

    connectToRoom();

    return () => {
      cancelled = true;
      isConnectedRef.current = false;
      unsubscribeFns.forEach((unsubscribe) => unsubscribe());

      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }

      setState({
        messages: [],
        isConnected: false,
        isConnecting: false,
        isLoadingHistory: false,
        error: null,
      });
    };
  }, [conversationId, enableRealtime, safeSetState]);

  const sendMessage = useCallback(async (content: string) => {
    if (!roomRef.current || !isConnectedRef.current) {
      throw new Error("Chat room is not connected");
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const request: SendMessageRequest = {
      action: "SEND_MESSAGE",
      content,
      requestId,
      attributes: {
        requestId,
      },
    };

    setState((s) => ({
      ...s,
      messages: mergeChatMessages(s.messages, {
        requestId,
        content,
        sendTime: new Date(),
        attributes: {
          requestId,
          isCurator: "true",
        },
      }),
    }));

    try {
      const response = await roomRef.current.sendMessage(request);
      setState((s) => ({
        ...s,
        messages: mergeChatMessages(s.messages, {
          ...(response as unknown as Record<string, unknown>),
          requestId,
          content,
          attributes: {
            ...(response.attributes ?? {}),
            requestId,
            isCurator: "true",
          },
        }),
      }));
      onLiveMessageRef.current?.();
      return response;
    } catch (err) {
      setState((s) => ({
        ...s,
        messages: s.messages.filter(
          (message) => getMessageRequestId(message) !== requestId
        ),
      }));
      // eslint-disable-next-line no-console
      console.error("Failed to send message", err);
      throw err;
    }
  }, []);

  return {
    ...state,
    sendMessage,
  };
};
